import { FC, useState, useEffect, useCallback } from 'react'
import { Box, Button, Chip, Fade, Grow, IconButton, Paper, Stack, TextField, Typography, alpha } from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PublicIcon from '@mui/icons-material/Public'
import BusinessIcon from '@mui/icons-material/Business'
import { useTranslate, useNotify, useRedirect } from 'react-admin'
import { getJson, getUrl, postJson, putJson } from '../../providers/data'

import { Registry, TriggerConfig, ActionConfig, WizardData, TriggerDef, ActionDef, CategoryDef } from './wizard/types'
import { FLOW } from './wizard/tokens'
import { renderItemSummary, findDef, findCategoryIcon } from './wizard/utils'
import { Connector, AddNodeButton, FlowNode } from './wizard/FlowNode'
import SettingsNode from './wizard/SettingsNode'
import OrganizationCheckboxList from './wizard/OrganizationCheckboxList'
import PickerDrawer from './wizard/PickerDrawer'

interface AutomationRuleWizardProps {
  mode: 'create' | 'edit'
  initialData?: WizardData
  recordId?: string
}

const AutomationRuleWizard: FC<AutomationRuleWizardProps> = ({ mode, initialData, recordId }) => {
  const [registry, setRegistry] = useState<Registry | null>(null)
  const [saving, setSaving] = useState(false)
  const [pickerOpen, setPickerOpen] = useState<'trigger' | 'action' | null>(null)
  const notify = useNotify()
  const redirect = useRedirect()
  const translate = useTranslate()

  const [touched, setTouched] = useState(mode === 'edit')
  // eslint-disable-next-line camelcase
  const [data, setData] = useState<WizardData>(
    initialData ?? {
      // eslint-disable-next-line camelcase
      display_name: '', description: '', enabled: true,
      // eslint-disable-next-line camelcase
      scope: 'global', organization_ids: [], scope_tiers: [],
      triggers: [], actions: [],
      // eslint-disable-next-line camelcase
      cooldown_minutes: 0,
    }
  )

  useEffect(() => {
    void getJson<Registry>(`${getUrl()}/automation_rule/registry`).then(setRegistry)
  }, [])

  const handleDataChange = useCallback((field: keyof WizardData, value: unknown) => {
    setTouched(true)
    setData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleAddTrigger = useCallback((trigger: TriggerConfig) => {
    setData((prev) => ({ ...prev, triggers: [...prev.triggers, trigger] }))
  }, [])

  const handleRemoveTrigger = useCallback((index: number) => {
    setData((prev) => ({ ...prev, triggers: prev.triggers.filter((_, i) => i !== index) }))
  }, [])

  const handleAddAction = useCallback((action: ActionConfig) => {
    setData((prev) => ({ ...prev, actions: [...prev.actions, action] }))
  }, [])

  const handleRemoveAction = useCallback((index: number) => {
    setData((prev) => ({ ...prev, actions: prev.actions.filter((_, i) => i !== index) }))
  }, [])

  const handleSave = async () => {
    if (!data.display_name.trim()) { notify(translate('automationRule.validation.nameRequired'), { type: 'warning' }); return }
    if (data.triggers.length === 0) { notify(translate('automationRule.validation.triggerRequired'), { type: 'warning' }); return }
    if (data.actions.length === 0) { notify(translate('automationRule.validation.actionRequired'), { type: 'warning' }); return }

    setSaving(true)
    try {
      const url = mode === 'edit' && recordId
        ? `${getUrl()}/automation_rule/${recordId}`
        : `${getUrl()}/automation_rule`

      if (mode === 'edit' && recordId) {
        await putJson(url, data)
      } else {
        await postJson(url, data)
      }

      notify(mode === 'create' ? translate('automationRule.created') : translate('automationRule.updated'), { type: 'success' })
      redirect('list', 'automation_rule')
    } catch (err: unknown) {
      const apiError = err as { body?: { message?: string } }
      notify(apiError?.body?.message || translate('automationRule.saveFailed'), { type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (!registry) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Typography sx={{ color: FLOW.gray }}>{translate('automationRule.loading')}</Typography>
      </Box>
    )
  }

  const triggerDefs = registry.triggers.categories.flatMap((c) => c.triggers ?? [])
  const actionDefs = registry.actions.categories.flatMap((c) => c.actions ?? [])
  const allTriggerCategories = registry.triggers.categories as CategoryDef<TriggerDef | ActionDef>[]
  const allActionCategories = registry.actions.categories as CategoryDef<TriggerDef | ActionDef>[]
  const isValid = data.display_name.trim() && data.triggers.length > 0 && data.actions.length > 0

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', pb: 6 }}>
      <Fade in timeout={400}>
        <Paper elevation={0} sx={{ borderRadius: '16px', border: `1px solid ${FLOW.grayBorder}`, p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton size="small" onClick={() => redirect('list', 'automation_rule')}>
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                {mode === 'create' ? translate('automationRule.titleCreate') : translate('automationRule.titleEdit')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => void handleSave()}
              disabled={saving || !isValid}
              disableElevation
              sx={{
                'bgcolor': FLOW.blue, 'color': '#F2F2F2', 'borderRadius': '50px',
                'textTransform': 'none', 'fontWeight': 600, 'px': 3,
                '&:hover': { bgcolor: FLOW.blue, filter: 'brightness(0.9)' },
              }}
            >
              {translate('ra.action.save')}
            </Button>
          </Box>

          <Stack spacing={2}>
            <TextField
              label={translate('data.displayName')}
              value={data.display_name}
              onChange={(e) => handleDataChange('display_name', e.target.value)}
              fullWidth
              size="small"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  'borderRadius': '10px',
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: FLOW.blue,
                  },
                },
              }}
            />
            <TextField
              label={translate('data.description')}
              value={data.description}
              onChange={(e) => handleDataChange('description', e.target.value)}
              fullWidth
              multiline
              rows={2}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  'borderRadius': '10px',
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: FLOW.blue,
                  },
                },
              }}
            />

            <Box>
              <Typography variant="caption" sx={{
                color: FLOW.gray, fontWeight: 600, letterSpacing: '0.04em',
                textTransform: 'uppercase', fontSize: '0.65rem', mb: 1, display: 'block',
              }}>
                {translate('automationRule.scope')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {([
                  { value: 'global', label: translate('automationRule.scopeGlobal'), icon: <PublicIcon sx={{ fontSize: 18 }} /> },
                  {
                    // eslint-disable-next-line camelcase
                    value: 'specific_organizations',
                    label: translate('automationRule.scopeSpecific'),
                    icon: <BusinessIcon sx={{ fontSize: 18 }} />,
                  },
                ] as const).map((opt) => (
                  <Box
                    key={opt.value}
                    onClick={() => {
                      handleDataChange('scope', opt.value)
                      if (opt.value === 'global') handleDataChange('organization_ids', [])
                      if (opt.value === 'specific_organizations') handleDataChange('scope_tiers', [])
                    }}
                    sx={{
                      'flex': 1, 'display': 'flex', 'alignItems': 'center', 'gap': 1,
                      'px': 2, 'py': 1.25, 'borderRadius': '10px',
                      'border': `2px solid ${data.scope === opt.value ? FLOW.blue : FLOW.grayBorder}`,
                      'bgcolor': data.scope === opt.value ? alpha(FLOW.blue, 0.04) : 'transparent',
                      'cursor': 'pointer', 'transition': 'all 0.15s ease', 'userSelect': 'none',
                      '&:hover': {
                        borderColor: data.scope === opt.value ? FLOW.blue : 'rgba(0,0,0,0.15)',
                        bgcolor: data.scope === opt.value ? alpha(FLOW.blue, 0.06) : FLOW.grayBg,
                      },
                    }}
                  >
                    <Box sx={{ color: data.scope === opt.value ? FLOW.blue : FLOW.gray }}>{opt.icon}</Box>
                    <Typography variant="body2" sx={{
                      fontWeight: data.scope === opt.value ? 600 : 500,
                      color: data.scope === opt.value ? FLOW.blue : 'rgba(0,0,0,0.65)',
                      fontSize: '0.82rem',
                    }}>
                      {opt.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {data.scope === 'global' && (
              <Box>
                <Typography variant="caption" sx={{
                  color: FLOW.gray, fontWeight: 600, letterSpacing: '0.04em',
                  textTransform: 'uppercase', fontSize: '0.65rem', mb: 1, display: 'block',
                }}>
                  {translate('automationRule.tierFilter')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {(['free', 'team', 'enterprise'] as const).map((tier) => {
                    const selected = data.scope_tiers.includes(tier)
                    return (
                      <Chip
                        key={tier}
                        label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                        size="small"
                        onClick={() => {
                          const next = selected
                            ? data.scope_tiers.filter((t) => t !== tier)
                            : [...data.scope_tiers, tier]
                          handleDataChange('scope_tiers', next)
                        }}
                        sx={{
                          'height': 28, 'fontSize': '0.78rem', 'fontWeight': 600,
                          'borderRadius': '8px', 'cursor': 'pointer',
                          ...(selected
                            ? { bgcolor: alpha(FLOW.blue, 0.12), color: FLOW.blue, border: `1px solid ${alpha(FLOW.blue, 0.3)}` }
                            : { bgcolor: 'transparent', color: FLOW.gray, border: `1px solid ${FLOW.grayBorder}` }),
                          '&:hover': { bgcolor: alpha(FLOW.blue, 0.08) },
                        }}
                      />
                    )
                  })}
                </Box>
              </Box>
            )}

            {data.scope === 'specific_organizations' && (
              <OrganizationCheckboxList
                selectedIds={data.organization_ids}
                onChange={(ids) => handleDataChange('organization_ids', ids)}
              />
            )}
          </Stack>
        </Paper>
      </Fade>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Grow in timeout={300}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.75,
              borderRadius: '20px',
              bgcolor: alpha(FLOW.blue, 0.08),
              border: `1px solid ${alpha(FLOW.blue, 0.15)}`,
            }}
          >
            <Box
              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: FLOW.blue }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: FLOW.blue,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
              }}
            >
              {translate('automationRule.flow.when')}
            </Typography>
          </Box>
        </Grow>

        {data.triggers.map((t, idx) => (
          <Box key={`t-${idx}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Connector color={FLOW.blue} />
            <FlowNode
              type="trigger" index={idx}
              label={findDef(t, triggerDefs)?.label ?? t.type}
              summary={renderItemSummary(t, triggerDefs)}
              categoryIcon={findCategoryIcon(t, allTriggerCategories)}
              onRemove={() => handleRemoveTrigger(idx)}
              animDelay={idx * 80}
            />
          </Box>
        ))}

        <AddNodeButton onClick={() => setPickerOpen('trigger')} label={translate('automationRule.flow.addTrigger')} color={FLOW.blue} />

        <Grow in timeout={300} style={{ transitionDelay: '100ms' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.75,
              borderRadius: '20px',
              bgcolor: alpha(FLOW.green, 0.08),
              border: `1px solid ${alpha(FLOW.green, 0.15)}`,
            }}
          >
            <Box
              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: FLOW.green }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: FLOW.green,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
              }}
            >
              {translate('automationRule.flow.then')}
            </Typography>
          </Box>
        </Grow>

        {data.actions.map((a, idx) => (
          <Box key={`a-${idx}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Connector color={FLOW.green} />
            <FlowNode
              type="action" index={idx}
              label={findDef(a, actionDefs)?.label ?? a.type}
              summary={renderItemSummary(a, actionDefs)}
              categoryIcon={findCategoryIcon(a, allActionCategories)}
              onRemove={() => handleRemoveAction(idx)}
              animDelay={idx * 80}
            />
          </Box>
        ))}

        <AddNodeButton onClick={() => setPickerOpen('action')} label={translate('automationRule.flow.addAction')} color={FLOW.green} />

        <SettingsNode data={data} onChange={handleDataChange} translate={translate} />

        <Connector dashed />
        <Grow in timeout={300} style={{ transitionDelay: '200ms' }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75,
            borderRadius: '20px', bgcolor: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.08)',
          }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: FLOW.gray }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: FLOW.gray, letterSpacing: '0.03em', fontSize: '0.65rem' }}>
              {translate('automationRule.flow.done')}
            </Typography>
          </Box>
        </Grow>
      </Box>

      {pickerOpen && (
        <PickerDrawer
          open={!!pickerOpen}
          onClose={() => setPickerOpen(null)}
          type={pickerOpen}
          registry={registry}
          onAdd={(item) => {
            if (pickerOpen === 'trigger') handleAddTrigger(item as TriggerConfig)
            else handleAddAction(item as ActionConfig)
          }}
        />
      )}

      {touched && !isValid && (
        <Fade in timeout={600} style={{ transitionDelay: '800ms' }}>
          <Paper
            elevation={0}
            sx={{
              position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
              px: 3, py: 1.5, borderRadius: '12px',
              bgcolor: 'rgba(0,0,0,0.82)', color: '#fff',
              display: 'flex', alignItems: 'center', gap: 1.5,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              zIndex: 1200,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>
              {!data.display_name.trim()
                ? translate('automationRule.validation.nameRequired')
                : data.triggers.length === 0
                  ? translate('automationRule.validation.triggerRequired')
                  : translate('automationRule.validation.actionRequired')}
            </Typography>
          </Paper>
        </Fade>
      )}
    </Box>
  )
}

export default AutomationRuleWizard
